/**
 * ReviewDashboard — the Review User's landing page: analytics only.
 *
 * Redesigned 05-10-2026 on the product owner's direction: this page answers
 * "what is on my plate and how am I doing", and the request TABLES moved to
 * the per-portal pages (ReviewPortalRequests, linked from the sidebar). Every
 * number here is a link into the portal page that holds those requests, so
 * the dashboard stays a summary and never duplicates the queue.
 *
 * Scope mirrors the queue exactly (the BE applies reviewer_may_view): the
 * reviewer's own requests plus the open ones of the portals they hold.
 * Delivery and speed figures are personal.
 *
 * Data: GET /api/v1/managed-service/reviewer/stats
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Alert,
  Button,
  Card,
  Empty,
  Skeleton,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import {
  ArrowRightOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  InboxOutlined,
  ReloadOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RTooltip,
  XAxis,
  YAxis,
} from "recharts";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { fetchData } from "../../api/apiService";
import { MANAGED_SERVICE } from "../../api/endpointMap";
import { PORTAL_PAGES, portalLabel } from "./portals";

dayjs.extend(relativeTime);

const { Title, Paragraph, Text } = Typography;

// Series colours are drawn from the app's chart palette and were validated
// for colour-blind separation (all pairs pass; worst dE 10.3 deutan): the
// workload states are cyan / amber / violet / pink, the two activity series
// blue / green. Colour follows the state, never its size, so a quiet day
// never repaints the chart.
const STATE_SERIES = [
  { key: "open", label: "Open", color: "#0891b2" },
  { key: "assigned", label: "Awaiting your acceptance", color: "#d97706" },
  { key: "accepted", label: "Accepted by you", color: "#7c3aed" },
  // Administrators also see requests held by other reviewers; without this
  // series their chart showed a total with no bars.
  { key: "others", label: "With other reviewers", color: "#db2777" },
];
const ACTIVITY_SERIES = [
  { key: "raised", label: "Raised", color: "#2563eb" },
  { key: "delivered", label: "Delivered by you", color: "#16a34a" },
];
const STAGE_COLOR = "#2563eb"; // single-series magnitude: one hue, no legend

const AXIS = { stroke: "#94a3b8", fontSize: 12 };
const GRID = "#e2e8f0";

const fmtHours = (h) => {
  if (h === null || h === undefined) return "-";
  if (h < 1) return `${Math.round(h * 60)} min`;
  if (h < 48) return `${h} h`;
  return `${(h / 24).toFixed(1)} days`;
};

/** One headline number. Clickable when it points at a portal page. */
const StatTile = ({ title, value, hint, icon, tone = "default", to, loading }) => {
  const body = (
    <Card
      size="small"
      hoverable={Boolean(to)}
      className="h-full"
      styles={{ body: { padding: 16 } }}
    >
      {loading ? (
        <Skeleton active paragraph={false} title={{ width: "70%" }} />
      ) : (
        <>
          <Space className="text-slate-500 text-xs mb-1">
            {icon}
            <span>{title}</span>
          </Space>
          <div
            className="text-2xl font-semibold leading-tight"
            style={{
              color:
                tone === "warn" && value > 0
                  ? "#b45309"
                  : tone === "good"
                    ? "#15803d"
                    : "#0f172a",
            }}
          >
            {value ?? "-"}
          </div>
          {hint && (
            <Text type="secondary" className="text-xs block mt-1">
              {hint}
            </Text>
          )}
        </>
      )}
    </Card>
  );
  return to ? (
    <Link to={to} className="block h-full">
      {body}
    </Link>
  ) : (
    body
  );
};

export default function ReviewDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const raw = await fetchData(MANAGED_SERVICE.REVIEWER_STATS);
      setStats(raw?.result || raw || null);
    } catch (e) {
      setError(e?.detail || e?.message || "Failed to load the dashboard");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const cards = stats?.cards || {};
  const byPortal = useMemo(() => stats?.by_portal || [], [stats]);
  const byStage = useMemo(
    () => (stats?.by_stage || []).filter((s) => s.count > 0),
    [stats],
  );
  const activity = useMemo(
    () =>
      (stats?.activity || []).map((d) => ({
        ...d,
        day: dayjs(d.date).format("DD MMM"),
      })),
    [stats],
  );

  const totalLive = byPortal.reduce((n, p) => n + (p.total || 0), 0);
  // Only series that actually carry a value are drawn, so the legend never
  // lists a state nobody has (a reviewer never sees "With other reviewers").
  const activeStateSeries = useMemo(
    () => STATE_SERIES.filter((s) => byPortal.some((p) => (p[s.key] || 0) > 0)),
    [byPortal],
  );
  const hasActivity = activity.some((d) => d.raised || d.delivered);
  // Per-reviewer workload. The backend fills this for an administrator only,
  // so an empty list IS the permission check - no role test needed here.
  const byReviewer = useMemo(() => stats?.by_reviewer || [], [stats]);
  // The portal with the most waiting work — where the "Open requests" tile
  // and the empty states should send the reviewer.
  const busiestSlug = useMemo(() => {
    const top = [...byPortal].sort(
      (a, b) => b.open + b.assigned - (a.open + a.assigned),
    )[0];
    const meta = PORTAL_PAGES.find((p) => p.portal === top?.portal);
    return meta ? `/review-dashboard/${meta.slug}` : null;
  }, [byPortal]);

  return (
    // Chrome (sidebar/header/background/padding) comes from ReviewerLayout.
    <div>
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div>
            <Title level={3} className="mb-1!">
              Review Dashboard
            </Title>
            <Paragraph className="mb-0! text-slate-500">
              Your response-generation workload at a glance. Open a portal from
              the sidebar to work its requests.
            </Paragraph>
          </div>
          <Space>
            {stats?.window_days && (
              <Tag color="geekblue" className="text-sm px-3 py-1">
                last {stats.window_days} days
              </Tag>
            )}
            <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>
              Refresh
            </Button>
          </Space>
        </div>

        {error && (
          <Alert
            type="error"
            showIcon
            className="mb-4"
            message="Failed to load the dashboard"
            description={error}
          />
        )}

        {/* ── Headline numbers ── */}
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-4">
          <StatTile
            loading={loading}
            title="Awaiting your acceptance"
            value={cards.awaiting_my_acceptance}
            tone="warn"
            icon={<ClockCircleOutlined />}
            to={busiestSlug}
            hint={
              cards.oldest_waiting_since
                ? `oldest since ${dayjs(cards.oldest_waiting_since).format("DD MMM, HH:mm")}`
                : "nothing waiting on you"
            }
          />
          <StatTile
            loading={loading}
            title="Accepted by you"
            value={cards.accepted_by_me}
            icon={<CheckCircleOutlined />}
            to={busiestSlug}
            hint="in your hands right now"
          />
          <StatTile
            loading={loading}
            title="Open in your portals"
            value={cards.open_in_my_portals}
            icon={<InboxOutlined />}
            to={busiestSlug}
            hint="anyone on the portal can take these"
          />
          <StatTile
            loading={loading}
            title="Delivered by you"
            value={cards.delivered_by_me}
            tone="good"
            icon={<ThunderboltOutlined />}
            hint={`in the last ${stats?.window_days || 14} days`}
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
          <StatTile
            loading={loading}
            title="Average time you take to accept"
            value={fmtHours(cards.avg_accept_hours)}
            hint="from assignment to acceptance"
          />
          <StatTile
            loading={loading}
            title="Average time to deliver"
            value={fmtHours(cards.avg_delivery_hours)}
            hint="from the subscriber's request to delivery"
          />
        </div>

        {/* ── Workload by portal ── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
          <Card
            size="small"
            title="Workload by portal"
            extra={
              busiestSlug && (
                <Button
                  type="link"
                  size="small"
                  onClick={() => navigate(busiestSlug)}
                >
                  Open requests <ArrowRightOutlined />
                </Button>
              )
            }
          >
            {loading ? (
              <Skeleton active paragraph={{ rows: 4 }} />
            ) : totalLive === 0 ? (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  <span className="text-slate-500">
                    No live requests in your portals.
                  </span>
                }
              />
            ) : (
              <div style={{ width: "100%", height: 260 }}>
                <ResponsiveContainer>
                  <BarChart
                    data={byPortal}
                    margin={{ top: 8, right: 16, bottom: 4, left: 0 }}
                    barSize={38}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke={GRID} vertical={false} />
                    <XAxis dataKey="label" tickLine={false} axisLine={false} {...AXIS} />
                    <YAxis allowDecimals={false} tickLine={false} axisLine={false} {...AXIS} />
                    <RTooltip
                      cursor={{ fill: "rgba(148,163,184,.12)" }}
                      contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0" }}
                    />
                    <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
                    {activeStateSeries.map((s, i) => (
                      <Bar
                        key={s.key}
                        dataKey={s.key}
                        name={s.label}
                        stackId="load"
                        fill={s.color}
                        // 2px surface gap between stacked segments, and only
                        // the top segment gets the rounded data-end.
                        stroke="#ffffff"
                        strokeWidth={2}
                        isAnimationActive={false}
                        radius={i === activeStateSeries.length - 1 ? [4, 4, 0, 0] : 0}
                      />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </Card>

          {/* ── My accepted work, by drafting stage ── */}
          <Card size="small" title="Your accepted work by stage">
            {loading ? (
              <Skeleton active paragraph={{ rows: 4 }} />
            ) : byStage.length === 0 ? (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  <span className="text-slate-500">
                    Nothing accepted yet. Accept a request to start drafting.
                  </span>
                }
              />
            ) : (
              <div style={{ width: "100%", height: 260 }}>
                <ResponsiveContainer>
                  <BarChart
                    data={byStage}
                    layout="vertical"
                    margin={{ top: 8, right: 24, bottom: 4, left: 8 }}
                    barSize={22}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke={GRID} horizontal={false} />
                    <XAxis
                      type="number"
                      allowDecimals={false}
                      tickLine={false}
                      axisLine={false}
                      {...AXIS}
                    />
                    <YAxis
                      type="category"
                      dataKey="label"
                      width={140}
                      tickLine={false}
                      axisLine={false}
                      {...AXIS}
                    />
                    <RTooltip
                      cursor={{ fill: "rgba(148,163,184,.12)" }}
                      contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0" }}
                    />
                    <Bar
                      dataKey="count"
                      name="Requests"
                      radius={[0, 4, 4, 0]}
                      isAnimationActive={false}
                    >
                      {byStage.map((s) => (
                        <Cell key={s.status} fill={STAGE_COLOR} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </Card>
        </div>

        {/* ── Who is carrying what (administrators only) ──
             A table, not a chart: these are names the administrator acts on,
             and the exact counts matter more than the shape. */}
        {byReviewer.length > 0 && (
          <Card
            size="small"
            className="mb-4"
            title="Workload by reviewer"
            extra={
              <Text type="secondary" className="text-xs">
                {stats?.unassigned_total || 0} unassigned
              </Text>
            }
          >
            <Table
              size="small"
              rowKey="id"
              pagination={false}
              dataSource={byReviewer}
              columns={[
                {
                  title: "Reviewer",
                  dataIndex: "name",
                  render: (name, row) => (
                    <div>
                      <div className="font-medium">
                        {name}{" "}
                        {!row.enabled && <Tag color="default">Disabled</Tag>}
                      </div>
                      <div className="text-xs text-slate-400">{row.email}</div>
                    </div>
                  ),
                },
                {
                  title: "Portals",
                  dataIndex: "portals",
                  render: (portals) => (
                    <Space size={4} wrap>
                      {(portals || []).map((p) => (
                        <Tag key={p} color="geekblue" className="m-0!">
                          {portalLabel(p)}
                        </Tag>
                      ))}
                      {(portals || []).length === 0 && (
                        <Text type="secondary" className="text-xs">
                          none
                        </Text>
                      )}
                    </Space>
                  ),
                },
                {
                  title: "Awaiting acceptance",
                  dataIndex: "awaiting_acceptance",
                  align: "right",
                  sorter: (a, b) => a.awaiting_acceptance - b.awaiting_acceptance,
                  render: (n, row) =>
                    n === 0 ? (
                      <Text type="secondary">0</Text>
                    ) : (
                      <Tooltip
                        title={
                          row.oldest_waiting_since
                            ? `Oldest assigned ${dayjs(row.oldest_waiting_since).fromNow()}`
                            : undefined
                        }
                      >
                        <Tag color="orange" className="m-0!">
                          {n}
                        </Tag>
                      </Tooltip>
                    ),
                },
                {
                  title: "In hand",
                  dataIndex: "in_hand",
                  align: "right",
                  sorter: (a, b) => a.in_hand - b.in_hand,
                  render: (n) =>
                    n === 0 ? <Text type="secondary">0</Text> : <strong>{n}</strong>,
                },
                {
                  title: "Live total",
                  dataIndex: "live_total",
                  align: "right",
                  defaultSortOrder: "descend",
                  sorter: (a, b) => a.live_total - b.live_total,
                },
                {
                  title: `Delivered (${stats?.window_days || 14}d)`,
                  dataIndex: "delivered",
                  align: "right",
                  sorter: (a, b) => a.delivered - b.delivered,
                  render: (n) => (n === 0 ? <Text type="secondary">0</Text> : n),
                },
              ]}
            />
            <Paragraph className="text-slate-400 text-xs mt-3 mb-0!">
              Awaiting acceptance is assigned work nobody has taken on yet.
              Open a request to reassign it, or release it back to the pool.
            </Paragraph>
          </Card>
        )}

        {/* ── Activity over the window ── */}
        <Card
          size="small"
          title={`Requests raised and delivered - last ${stats?.window_days || 14} days`}
        >
          {loading ? (
            <Skeleton active paragraph={{ rows: 4 }} />
          ) : !hasActivity ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <span className="text-slate-500">
                  No requests raised or delivered in this window.
                </span>
              }
            />
          ) : (
            <div style={{ width: "100%", height: 280 }}>
              <ResponsiveContainer>
                <LineChart
                  data={activity}
                  margin={{ top: 8, right: 24, bottom: 4, left: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={GRID} vertical={false} />
                  <XAxis dataKey="day" tickLine={false} axisLine={false} {...AXIS} />
                  <YAxis allowDecimals={false} tickLine={false} axisLine={false} {...AXIS} />
                  <RTooltip contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0" }} />
                  <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
                  {ACTIVITY_SERIES.map((s) => (
                    <Line
                      key={s.key}
                      // Daily counts are discrete: a straight segment between
                      // days, never a smoothed curve that implies values in
                      // between.
                      type="linear"
                      dataKey={s.key}
                      name={s.label}
                      stroke={s.color}
                      strokeWidth={2}
                      dot={{ r: 3, strokeWidth: 0, fill: s.color }}
                      activeDot={{ r: 5, strokeWidth: 2, stroke: "#ffffff" }}
                      isAnimationActive={false}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card>

        <Paragraph className="text-slate-400 text-xs mt-3 mb-0!">
          <Tooltip title="Open requests of your portals plus everything assigned to you">
            <span>Counts follow the same scope as your request pages.</span>
          </Tooltip>
        </Paragraph>
      </div>
    </div>
  );
}
